import { useEffect, useState } from 'react';
import { useParams, Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Loader2, BadgeCheck, Package, ShoppingCart, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../config/language';
import { useTheme } from '../config/theme';
import { useCart } from '../contexts/CartContext';
import { fetchMerchantItem } from '../hooks/useMerchantMarket';
import PriceDisplay from '../components/marketplace/PriceDisplay';
import StoreUidBadge from '../components/marketplace/StoreUidBadge';

const PLACEHOLDER = 'https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=800&q=60';

export default function ProductDetailsPage() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const cachedStore = location.state?.store || null;
  const cachedItem = location.state?.item || null;
  const { language } = useLanguage();
  const { isDarkMode } = useTheme();
  const { addToCart } = useCart();
  const isAr = language === 'ar';
  const [data, setData] = useState({ item: cachedItem, store: cachedStore });
  const [loading, setLoading] = useState(!cachedItem);
  const [activeImg, setActiveImg] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const BackIcon = isAr ? ArrowRight : ArrowLeft;

  useEffect(() => {
    let mounted = true;
    fetchMerchantItem('ecommerce', id, cachedStore)
      .then(res => { if (mounted) setData((prev) => ({ item: res.item || prev.item, store: res.store || prev.store })); })
      .finally(() => setLoading(false));
    return () => { mounted = false; };
  }, [id]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#1E9BD9]" /></div>;
  if (!data.item) return <div className="min-h-screen flex items-center justify-center text-red-500">Not found</div>;
  const { item, store } = data;
  const images = (item.images && item.images.length) ? item.images : [item.image || PLACEHOLDER];
  const title = isAr ? item.name : (item.nameEn || item.name);
  const inStock = !item.trackInventory || (item.quantity || 0) > 0;

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-[#0a0a0a]' : 'bg-[#F8FAFC]'}`} dir={isAr ? 'rtl' : 'ltr'}>
      <div className="max-w-5xl mx-auto px-4 py-8">
        <Link to="/products" className="inline-flex items-center gap-1.5 text-sm text-[#1E9BD9] hover:underline mb-6" data-testid="back-link">
          <BackIcon className="h-4 w-4" />
          {isAr ? 'العودة إلى المتجر' : 'Back to Products'}
        </Link>

        <div className={`rounded-3xl overflow-hidden ${isDarkMode ? 'bg-[#141414] border border-white/5' : 'bg-white border border-[#E2E8F0] shadow-lg'}`}>
          <div className="grid md:grid-cols-2 gap-0">
            <div className="bg-slate-100 dark:bg-white/5">
              <div className="aspect-square overflow-hidden">
                <img src={images[activeImg]} alt={title} className="w-full h-full object-cover" />
              </div>
              {images.length > 1 && (
                <div className="flex gap-2 p-3 overflow-x-auto">
                  {images.map((src, i) => (
                    <button key={i} onClick={() => setActiveImg(i)}
                      className={`shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 ${i === activeImg ? 'border-[#1E9BD9]' : 'border-transparent'}`}>
                      <img src={src} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="p-6 md:p-8">
              <div className="flex items-center gap-2 mb-2">
                {item.brand && <span className="text-xs uppercase tracking-wide text-slate-500">{item.brand}</span>}
                {item.store?.verified && <BadgeCheck className="h-4 w-4 text-[#1E9BD9]" />}
              </div>
              <h1 className="text-2xl md:text-3xl font-bold text-[#0F172A] dark:text-white mb-3">{title}</h1>

              <div className="mb-4">
                <PriceDisplay item={item} locale={isAr ? 'ar' : 'en'} size="lg" />
              </div>

              <div className="flex items-center gap-3 mb-4 text-sm">
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${
                  inStock ? 'bg-green-500/10 text-green-700 dark:text-green-300' : 'bg-red-500/10 text-red-700 dark:text-red-300'
                }`}>
                  <Package className="h-3 w-3" />
                  {inStock ? (isAr ? 'متوفر' : 'In stock') : (isAr ? 'نفدت الكمية' : 'Out of stock')}
                </span>
                {item.sku && <span className="text-xs text-slate-500">SKU: <span dir="ltr">{item.sku}</span></span>}
              </div>

              {Array.isArray(item.colors) && item.colors.length > 0 && (
                <div className="mb-4">
                  <p className="text-xs font-semibold mb-2 text-slate-500">{isAr ? 'الألوان' : 'Colors'}</p>
                  <div className="flex flex-wrap gap-2">
                    {item.colors.map((c, i) => (
                      <span key={i} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs bg-slate-100 dark:bg-white/10">
                        <span className="w-3 h-3 rounded-full border border-slate-300" style={{ background: c.hex || c }} />
                        {c.name || c}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {Array.isArray(item.sizes) && item.sizes.length > 0 && (
                <div className="mb-4">
                  <p className="text-xs font-semibold mb-2 text-slate-500">{isAr ? 'الأحجام / الإصدارات' : 'Sizes / Variants'}</p>
                  <div className="flex flex-wrap gap-2">
                    {item.sizes.map((s, i) => (
                      <span key={i} className="px-2.5 py-1 rounded-full text-xs bg-slate-100 dark:bg-white/10">{s}</span>
                    ))}
                  </div>
                </div>
              )}

              {item.description && (
                <div className={`rounded-xl p-4 mb-6 text-sm leading-relaxed ${isDarkMode ? 'bg-white/5 text-white/80' : 'bg-slate-50 text-slate-700'}`}>
                  {isAr ? item.description : (item.descriptionEn || item.description)}
                </div>
              )}

              {store?.id && (
                <div className={`rounded-xl p-3 mb-4 flex items-center gap-3 ${isDarkMode ? 'bg-white/5 border border-white/10' : 'bg-slate-50 border border-slate-200'}`}>
                  <Link to={`/store/${store.id}`} className="flex items-center gap-3 min-w-0" data-testid="store-strip">
                    {store.logo && <img src={store.logo} alt="" className="w-9 h-9 rounded-lg object-cover" />}
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="text-sm font-semibold text-[#0F172A] dark:text-white truncate">
                          {isAr ? (store.name || store.nameEn) : (store.nameEn || store.name)}
                        </span>
                        {store.verified && <BadgeCheck className="h-3.5 w-3.5 text-[#1E9BD9] shrink-0" />}
                      </div>
                      <StoreUidBadge store={store} locale={isAr ? 'ar' : 'en'} className="mt-0.5" />
                    </div>
                  </Link>
                </div>
              )}

              <ContactActionsEcommerce
                item={item}
                store={store}
                inStock={inStock}
                quantity={quantity}
                setQuantity={setQuantity}
                onAddToCart={() => {
                  const payload = {
                    ...item,
                    store_id: store?.id,
                    store_name: store?.name || store?.nameEn,
                    image: images[0],
                    stock: item.trackInventory ? (item.quantity || 0) : 999,
                  };
                  addToCart(payload, quantity);
                  toast.success(isAr ? 'تمت الإضافة إلى السلة' : 'Added to cart');
                }}
                onBuyNow={() => {
                  const payload = {
                    ...item,
                    store_id: store?.id,
                    store_name: store?.name || store?.nameEn,
                    image: images[0],
                    stock: item.trackInventory ? (item.quantity || 0) : 999,
                  };
                  addToCart(payload, quantity);
                  navigate('/checkout');
                }}
                isAr={isAr}
                isDarkMode={isDarkMode}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// E-commerce action strip — Add to Cart + Buy Now
// (Replaces the classifieds-style Call/WhatsApp/Email actions)
// ─────────────────────────────────────────────────────────
function ContactActionsEcommerce({ item, inStock, quantity, setQuantity, onAddToCart, onBuyNow, isAr }) {
  const maxQty = item?.trackInventory ? Math.max(1, item.quantity || 1) : 99;
  return (
    <div className="space-y-3">
      {/* Quantity stepper */}
      <div className="flex items-center gap-3">
        <span className="text-sm font-semibold text-slate-600 dark:text-slate-300">
          {isAr ? 'الكمية' : 'Quantity'}
        </span>
        <div className="inline-flex items-center rounded-xl border border-slate-200 dark:border-white/10 overflow-hidden">
          <button
            type="button"
            data-testid="product-qty-decrement"
            onClick={() => setQuantity((q) => Math.max(1, q - 1))}
            disabled={!inStock || quantity <= 1}
            className="w-9 h-9 flex items-center justify-center text-lg font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-white/5 disabled:opacity-40"
          >−</button>
          <span className="w-10 text-center font-bold text-slate-900 dark:text-white" data-testid="product-qty-value">{quantity}</span>
          <button
            type="button"
            data-testid="product-qty-increment"
            onClick={() => setQuantity((q) => Math.min(maxQty, q + 1))}
            disabled={!inStock || quantity >= maxQty}
            className="w-9 h-9 flex items-center justify-center text-lg font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-white/5 disabled:opacity-40"
          >+</button>
        </div>
      </div>

      {/* Action buttons */}
      <div className="grid grid-cols-2 gap-3">
        <button
          type="button"
          data-testid="product-add-to-cart"
          onClick={onAddToCart}
          disabled={!inStock}
          className="h-12 rounded-xl font-bold flex items-center justify-center gap-2 border-2 border-[#1E9BD9] text-[#1E9BD9] hover:bg-[#1E9BD9]/5 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <ShoppingCart className="h-5 w-5" />
          {isAr ? 'أضف إلى السلة' : 'Add to cart'}
        </button>
        <button
          type="button"
          data-testid="product-buy-now"
          onClick={onBuyNow}
          disabled={!inStock}
          className="h-12 rounded-xl font-bold flex items-center justify-center gap-2 bg-gradient-to-r from-[#1E9BD9] to-[#0EA5E9] text-white shadow-md hover:opacity-95 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Zap className="h-5 w-5" />
          {isAr ? 'اشترِ الآن' : 'Buy now'}
        </button>
      </div>
    </div>
  );
}
